import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, LogOut, Users, GraduationCap, Heart, ShieldCheck, FileText, Download } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [pending, setPending] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);

  const load = async () => {
    const [ov, p, a, t, pa] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/students?status=pending"),
      api.get("/admin/students?status=assigned"),
      api.get("/admin/users?role=teacher"),
      api.get("/admin/users?role=parent"),
    ]);
    setOverview(ov.data);
    setPending(p.data);
    setAssigned(a.data);
    setTeachers(t.data);
    setParents(pa.data);
  };

  useEffect(() => { load(); }, []);

  const assign = async (studentId, teacherId) => {
    await api.put(`/admin/students/${studentId}/assign`, { teacher_id: teacherId || null });
    toast.success(teacherId ? "Student assigned" : "Student unassigned");
    load();
  };

  return (
    <div className="min-h-screen bg-[#FBFBF9]" data-testid="admin-dashboard">
      <header className="border-b border-[#E8E9E6] bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#6E5AA0] flex items-center justify-center">
              <Sparkles className="text-white" size={16} />
            </div>
            <div>
              <div className="font-heading text-lg font-semibold ink leading-none">Ellis Path</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6B726F] mt-1">Admin console</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium ink">{user?.name}</div>
              <div className="text-xs ink-muted">{user?.email}</div>
            </div>
            <Button variant="ghost" onClick={() => { logout(); navigate("/login"); }} className="text-[#6B726F] hover:text-[#BA6356]" data-testid="admin-logout">
              <LogOut size={16} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-10 space-y-8">
        <section>
          <div className="overline">Platform</div>
          <h1 className="font-heading text-4xl font-medium ink mt-3">Admin overview</h1>
          <p className="ink-muted mt-2">Assign parent-created students to teachers and keep an eye on the platform.</p>
        </section>

        <section className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat icon={GraduationCap} label="Teachers" value={overview?.counts.teachers ?? "—"} testid="stat-teachers" />
          <Stat icon={Heart} label="Parents" value={overview?.counts.parents ?? "—"} testid="stat-parents" />
          <Stat icon={Users} label="Students pending" value={overview?.counts.students_pending ?? "—"} testid="stat-pending" highlight />
          <Stat icon={Users} label="Students assigned" value={overview?.counts.students_assigned ?? "—"} testid="stat-assigned" />
          <Stat icon={FileText} label="Documents" value={overview?.counts.documents ?? "—"} testid="stat-docs" />
        </section>

        <Tabs defaultValue="pending">
          <TabsList className="bg-white border border-[#E8E9E6] rounded-xl p-1 h-auto flex-wrap">
            <TabsTrigger value="pending" data-testid="admin-tab-pending" className="rounded-lg px-4 py-2 data-[state=active]:bg-[#6E5AA0] data-[state=active]:text-white">Pending students</TabsTrigger>
            <TabsTrigger value="assigned" data-testid="admin-tab-assigned" className="rounded-lg px-4 py-2 data-[state=active]:bg-[#6E5AA0] data-[state=active]:text-white">Assigned students</TabsTrigger>
            <TabsTrigger value="teachers" data-testid="admin-tab-teachers" className="rounded-lg px-4 py-2 data-[state=active]:bg-[#6E5AA0] data-[state=active]:text-white">Teachers</TabsTrigger>
            <TabsTrigger value="parents" data-testid="admin-tab-parents" className="rounded-lg px-4 py-2 data-[state=active]:bg-[#6E5AA0] data-[state=active]:text-white">Parents</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-6 space-y-4">
            {pending.length === 0 ? (
              <Card className="p-8 border-dashed border-[#D8D9D5] bg-white text-center">
                <p className="ink-muted">No pending students. New parent signups will appear here.</p>
              </Card>
            ) : pending.map((s) => (
              <StudentRow key={s.id} student={s} teachers={teachers} onAssign={assign} />
            ))}
          </TabsContent>

          <TabsContent value="assigned" className="mt-6 space-y-4">
            {assigned.length === 0 ? (
              <Card className="p-8 border-dashed border-[#D8D9D5] bg-white text-center">
                <p className="ink-muted">No assigned students yet.</p>
              </Card>
            ) : assigned.map((s) => (
              <StudentRow key={s.id} student={s} teachers={teachers} onAssign={assign} />
            ))}
          </TabsContent>

          <TabsContent value="teachers" className="mt-6">
            <UserList users={teachers} emptyMsg="No teachers yet." />
          </TabsContent>

          <TabsContent value="parents" className="mt-6">
            <UserList users={parents} emptyMsg="No parents yet." />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid, highlight }) {
  return (
    <Card className={`p-4 border bg-white ${highlight ? "border-[#6E5AA0] ring-1 ring-[#6E5AA0]/20" : "border-[#E8E9E6]"}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">{label}</div>
          <div className="font-heading text-2xl font-medium ink mt-1.5">{value}</div>
        </div>
        <div className="w-8 h-8 rounded-lg bg-[#F0EBF6] flex items-center justify-center">
          <Icon size={14} className="text-[#6E5AA0]" strokeWidth={1.75} />
        </div>
      </div>
    </Card>
  );
}

function StudentRow({ student, teachers, onAssign }) {
  const [docs, setDocs] = useState(null);
  const [showDocs, setShowDocs] = useState(false);

  const toggleDocs = async () => {
    if (!showDocs && docs === null) {
      const r = await api.get(`/documents/student/${student.id}`);
      setDocs(r.data);
    }
    setShowDocs((v) => !v);
  };

  const download = async (doc) => {
    try {
      const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download");
    }
  };

  const isAssigned = !!student.user_id;
  return (
    <Card className="p-5 border border-[#E8E9E6] bg-white" data-testid={`admin-student-${student.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading text-xl font-medium ink">{student.first_name} {student.last_name}</div>
            <Badge variant="outline" className="bg-[#F0EBF6]">Grade {student.grade}</Badge>
            {isAssigned ? (
              <Badge className="bg-[#E1E9DE] text-[#3C5C40] hover:bg-[#E1E9DE]">Assigned</Badge>
            ) : (
              <Badge className="bg-[#F3E6D4] text-[#7C5B25] hover:bg-[#F3E6D4]">Pending</Badge>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
            {student.date_of_birth && <Info k="DOB" v={student.date_of_birth} />}
            {student.school && <Info k="School" v={student.school} />}
            {student.home_address && <Info k="Address" v={student.home_address} />}
            {student.created_by_parent && <Info k="Parent" v={`${student.created_by_parent.name} · ${student.created_by_parent.email}`} />}
            {student.assigned_teacher && <Info k="Teacher" v={`${student.assigned_teacher.name} · ${student.assigned_teacher.email}`} />}
          </div>
        </div>

        <div className="flex flex-col gap-2 min-w-[14rem]">
          <Label className="text-xs ink-muted">Assigned teacher</Label>
          <Select
            value={student.user_id || "none"}
            onValueChange={(v) => onAssign(student.id, v === "none" ? null : v)}
          >
            <SelectTrigger data-testid={`assign-select-${student.id}`}>
              <SelectValue placeholder="Choose a teacher" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned</SelectItem>
              {teachers.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={toggleDocs} data-testid={`view-docs-${student.id}`}>
            <FileText size={14} className="mr-2" /> {showDocs ? "Hide" : "View"} documents
          </Button>
        </div>
      </div>

      {showDocs && (
        <div className="mt-4 border-t border-[#EDEDE9] pt-4 space-y-2">
          {docs === null && <div className="text-sm ink-muted">Loading…</div>}
          {docs && docs.length === 0 && <div className="text-sm ink-muted">No documents uploaded for this student.</div>}
          {docs && docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 border border-[#EDEDE9] rounded-lg px-3 py-2 bg-[#FBFBF9]" data-testid={`doc-row-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={16} className="text-[#6E5AA0] shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm ink truncate">{d.original_filename}</div>
                  <div className="text-xs ink-muted">{d.doc_type} · uploaded by {d.uploaded_by_name} · {new Date(d.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => download(d)}>
                <Download size={14} className="mr-1" /> Download
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Info({ k, v }) {
  return (
    <div className="flex gap-2">
      <span className="ink-muted text-xs uppercase tracking-wider w-20 shrink-0 pt-0.5">{k}</span>
      <span className="ink">{v}</span>
    </div>
  );
}

function Label({ children, className }) {
  return <div className={`text-sm font-medium ${className || ""}`}>{children}</div>;
}

function UserList({ users, emptyMsg }) {
  if (users.length === 0) {
    return (
      <Card className="p-8 border-dashed border-[#D8D9D5] bg-white text-center">
        <p className="ink-muted">{emptyMsg}</p>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {users.map((u) => (
        <Card key={u.id} className="p-4 border border-[#E8E9E6] bg-white flex items-center justify-between" data-testid={`admin-user-${u.id}`}>
          <div>
            <div className="font-medium ink">{u.name}</div>
            <div className="text-xs ink-muted mt-0.5">{u.email} · {u.role}</div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-[#F0EBF6]">{u.role}</Badge>
            <div className="text-xs ink-muted">{new Date(u.created_at).toLocaleDateString()}</div>
          </div>
        </Card>
      ))}
    </div>
  );
}
